<template>
  <b-overlay :show="loading" rounded="sm" no-fade>
    <b-row class="mb-1">
      <b-col
        cols="12"
        md="7"
        class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
      >
        <h3 class="ml-1 mb-0">My E-Learning [CPD] Log</h3>
      </b-col>

      <b-col cols="12" md="5">
        <div class="d-flex align-items-center justify-content-end">
          <div class="d-flex align-items-center mr-1">
            <label class="mb-0 mr-50">E-Learning [CPD] year</label>
            <v-select
              v-model="year"
              :options="yearOptions"
              :clearable="false"
              class="year-selector d-inline-block"
            />
          </div>
          <b-button
            variant="primary"
            :disabled="loading || downloading"
            @click="exportPdf"
          >
            <b-spinner v-if="downloading" small class="mr-50" />
            <span class="text-nowrap">Export to PDF</span>
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-row class="mb-1">
      <b-col md="4" sm="6" class="mb-1">
        <b-card class="stat-tile mb-0">
          <h3 class="mb-25">{{ log.total_credits }}</h3>
          <small class="text-muted">Total CPD credits awarded</small>
        </b-card>
      </b-col>
      <b-col md="4" sm="6" class="mb-1">
        <b-card class="stat-tile mb-0">
          <h3 class="mb-25">{{ log.rows.length }}</h3>
          <small class="text-muted">Courses completed</small>
        </b-card>
      </b-col>
      <b-col md="4" sm="6" class="mb-1">
        <b-card class="stat-tile mb-0">
          <h3 class="mb-25">{{ log.total_hours }}</h3>
          <small class="text-muted">Hours to complete</small>
        </b-card>
      </b-col>
    </b-row>

    <b-table
      small
      striped
      hover
      bordered
      responsive
      :items="log.rows"
      :fields="fields"
      show-empty
      :empty-text="`No E-Learning [CPD] courses completed in ${year}.`"
    >
      <template #cell(cpd_credits)="{ value }">
        {{ value === null ? "-" : value }}
      </template>
      <template #cell(hours)="{ value }">
        {{ value === null ? "-" : value }}
      </template>
      <template #cell(grade)="{ value }">
        {{ value === null ? "-" : value + "%" }}
      </template>
    </b-table>

    <p class="text-muted ml-1" v-if="log.learner.division">
      Division: <strong>{{ log.learner.division }}</strong>
    </p>
  </b-overlay>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BTable,
  BOverlay,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BTable,
    BOverlay,
    BSpinner,
    vSelect,
    ToastificationContent,
  },
  data() {
    return {
      loading: false,
      downloading: false,
      year: new Date().getFullYear(),
      availableYears: [],
      log: {
        learner: { name: "", division: null },
        rows: [],
        total_credits: 0,
        total_hours: 0,
      },
      fields: [
        { key: "course", label: "Course/training completed", thStyle: "color:white!important" },
        { key: "completed_at", label: "Date completed", class: "text-center", thStyle: "color:white!important" },
        { key: "cpd_credits", label: "CPD Credits Awarded", class: "text-center", thStyle: "color:white!important" },
        { key: "hours", label: "Hours", class: "text-center", thStyle: "color:white!important" },
        { key: "grade", label: "Grade", class: "text-center", thStyle: "color:white!important" },
      ],
    };
  },
  computed: {
    // Always offer the current year, even before anything is logged in it.
    yearOptions() {
      const years = new Set(this.availableYears);
      years.add(new Date().getFullYear());
      years.add(this.year);
      return Array.from(years).sort((a, b) => b - a);
    },
  },
  watch: {
    year() {
      this.loadLog();
    },
  },
  created() {
    this.loadLog();
  },
  methods: {
    toast(title, variant = "danger", icon = "AlertTriangleIcon") {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      });
    },
    loadLog() {
      this.loading = true;
      store
        .dispatch("events/getMyCpdLog", { year: this.year })
        .then((res) => {
          this.log = res.data;
          if (res.data.available_years && res.data.available_years.length) {
            this.availableYears = res.data.available_years;
          }
        })
        .catch(() => this.toast("Could not load your E-Learning [CPD] log"))
        .finally(() => {
          this.loading = false;
        });
    },
    exportPdf() {
      this.downloading = true;
      store
        .dispatch("events/downloadMyCpdLogPdf", { year: this.year })
        .then((res) => {
          const disposition = res.headers && res.headers["content-disposition"];
          const match = disposition && disposition.match(/filename="?([^";]+)"?/);
          const filename = match ? match[1] : `cpd-log-${this.year}.pdf`;
          const url = window.URL.createObjectURL(
            new Blob([res.data], { type: "application/pdf" })
          );
          const a = document.createElement("a");
          a.href = url;
          a.setAttribute("download", filename);
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => this.toast("Could not generate the PDF"))
        .finally(() => {
          this.downloading = false;
        });
    },
  },
};
</script>

<style scoped>
.stat-tile { text-align: center; }
.year-selector { min-width: 110px; }
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
